import { User } from '../types';

// Queue for pending requests to handle rate limiting
interface QueuedRequest {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  execute: () => Promise<any>;
}

const requestQueue: QueuedRequest[] = [];
let isProcessingQueue = false;
const RATE_LIMIT_DELAY = 1000; // 1 second between requests roughly

const processQueue = async () => {
  if (isProcessingQueue || requestQueue.length === 0) return;
  isProcessingQueue = true;

  while (requestQueue.length > 0) {
    const request = requestQueue.shift();
    if (request) {
      try {
        const result = await request.execute();
        request.resolve(result);
      } catch (error) {
        request.reject(error);
      }
      // Wait before processing next to respect RPD/RPM
      await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY));
    }
  }
  isProcessingQueue = false;
};

// Smart Context Management - Enhanced for mobile
// Trims history to keep it relevant and within token limits
export const optimizeHistory = (history: any[], maxTokens = 30000) => {
    // Detect mobile environment
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isCapacitor = !!(window as any).Capacitor;
    
    if (history.length <= 4) return history;
    
    const systemMsg = history.filter(m => m.role === 'system');
    const lastMessages = history.slice(-4); // Last 4 are crucial
    const olderMessages = history.slice(0, -4).filter(m => m.role !== 'system');
    
    // Simple heuristic: filter out short acknowledgement messages from older history
    const meaningfulOlder = olderMessages.filter(m => {
        if (m.role === 'user' || m.role === 'model') {
            const textLength = m.parts[0]?.text?.length || 0;
            // Mobile: More aggressive filtering
            if (isMobile || isCapacitor) {
                return textLength > 30; // Keep only substantial messages
            }
            return textLength > 15; // Keep only substantial messages
        }
        return true;
    });
    
    // Mobile: Further compress older messages by truncating
    // SAFEGUARD: Only compress very old messages, not recent context
    if (isMobile || isCapacitor) {
        meaningfulOlder.forEach((m, index) => {
            // Keep last 2 meaningful messages uncompressed for quality
            const isVeryOld = index < meaningfulOlder.length - 2;
            if (isVeryOld && m.parts && m.parts[0] && m.parts[0].text && m.parts[0].text.length > 300) {
                // Keep first 300 chars which usually has the most important info
                m.parts[0].text = m.parts[0].text.substring(0, 300) + '...';
            }
        });
    }
    
    return [...systemMsg, ...meaningfulOlder, ...lastMessages];
};

// Estimate token count for payload optimization
export const estimateTokenCount = (text: string): number => {
    // Rough estimate: 1 token ≈ 4 characters
    return Math.ceil(text.length / 4);
};

// Compress message content for network transmission
export const compressMessageContent = (content: string, maxLength: number = 2000): string => {
    if (content.length <= maxLength) return content;
    
    // Smart truncation: Try to end at sentence boundary
    const truncated = content.substring(0, maxLength);
    const lastPeriod = Math.max(
        truncated.lastIndexOf('. '),
        truncated.lastIndexOf('! '),
        truncated.lastIndexOf('? ')
    );
    
    if (lastPeriod > maxLength * 0.8) {
        return truncated.substring(0, lastPeriod + 2) + '...[truncated]';
    }
    
    return truncated + '...[truncated]';
};
